import { decodePoster } from '@/scripts/core/media-poster'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type Hls } from '@/scripts/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { percentageSeen } from '@/scripts/theme/animations'
import { type ArtDirection } from '@/scripts/core/art-direction'
import { ambientMotionEnabled } from '@/scripts/core/motion'
import { hlsSourceHasAudio } from '@/scripts/core/hls-audio'

function isVideoPlaying(video: HTMLVideoElement): boolean {
	return !video.paused && !video.ended
}

function selectHlsVariant(
	manifest: string,
	masterUrl: URL,
	maxHeight: number
): string | undefined {
	const lines = manifest.split(/\r?\n/).map((line) => line.trim())
	let selectedHeight = 0
	let selectedUrl: string | undefined

	for (let index = 0; index < lines.length; index += 1) {
		const height = Number(
			lines[index].match(/RESOLUTION=\d+x(\d+)/)?.[1] ?? 0
		)
		if (height <= selectedHeight || height > maxHeight) continue

		const source = lines
			.slice(index + 1)
			.find((line) => line && !line.startsWith('#'))
		if (!source) continue
		selectedHeight = height
		selectedUrl = new URL(source, masterUrl).href
	}

	return selectedUrl
}

export const MOBILE_AUTOPLAY_HLS_MAX_HEIGHT = 480

function shouldHoldAutoplayForUserIntent(autoplayOnMobile: boolean): boolean {
	const isMobile = window.matchMedia('(max-width: 767px)').matches
	const connection = (
		navigator as Navigator & {
			connection?: {
				downlink?: number
				effectiveType?: string
				saveData?: boolean
			}
		}
	).connection
	const constrainedConnection =
		connection?.saveData === true ||
		connection?.effectiveType === 'slow-2g' ||
		connection?.effectiveType === '2g' ||
		connection?.effectiveType === '3g' ||
		(connection?.downlink !== undefined && connection.downlink < 2)

	return isMobile && (!autoplayOnMobile || constrainedConnection)
}

export class UcoastVideo extends UcoastEl {
	static htmlSelector = 'ucoast-video'
	get posterFirst() {
		return (
			this.closest('art-direction')?.hasAttribute('data-poster-first') ===
			true
		)
	}
	async waitUntilSettled(): Promise<void> {
		if (this.settled || this.dataset.ucHasPlayed === 'true') return
		await new Promise<void>((resolve) => {
			const observer = new MutationObserver(() => {
				if (
					this.settled ||
					this.dataset.ucHasPlayed === 'true' ||
					!this.isConnected ||
					!this.isTouchingViewport()
				)
					finish()
			})
			const finish = () => {
				observer.disconnect()
				window.clearTimeout(timer)
				resolve()
			}
			const timer = window.setTimeout(finish, 8000)
			observer.observe(this, {
				attributes: true,
				attributeFilter: ['data-media-state', 'data-uc-has-played'],
			})
		})
	}
	private preparation?: Promise<void>
	private lifecycle = new AbortController()
	private framePending = false
	private frameCallback?: number
	private frameTimer?: number
	private settled = false
	private playOnce = false
	private shortVideo = false

	async prepare(): Promise<void> {
		if (
			!this.posterFirst ||
			this.settled ||
			this.isHiddenForViewport() ||
			!this.isConnected
		)
			return
		this.preparation ??= this.boundedPreparation()
		return this.preparation
	}

	private async boundedPreparation() {
		let timer: number | undefined
		await Promise.race([
			this.prepareSelectedMedia(),
			new Promise<void>((resolve) => {
				timer = window.setTimeout(() => {
					if (this.isConnected) this.settlePoster('failed')
					resolve()
				}, 8000)
			}),
		])
		window.clearTimeout(timer)
	}

	private async prepareSelectedMedia(): Promise<void> {
		const wrapper = this.closest('art-direction')
		const image = wrapper?.querySelector('img')
		this.dataset.mediaState = 'loading'
		if (image) {
			image.style.visibility = ''
			const decoded = await decodePoster(image)
			if (decoded !== true || image.naturalWidth < 1) {
				image.style.visibility = 'hidden'
				const recover = () => {
					if (!this.isConnected || image.naturalWidth < 1) return
					void image.decode().then(
						() => {
							if (this.isConnected && image.naturalWidth > 0)
								image.style.visibility = ''
						},
						() => {}
					)
				}
				image.addEventListener(
					'load',
					recover,
					{ once: true }
				)
				if (image.complete && image.naturalWidth > 0) recover()
			}
		}
		if (
			!this.isConnected ||
			this.lifecycle.signal.aborted ||
			this.isHiddenForViewport()
		)
			return
		this.dataset.mediaState = image?.naturalWidth
			? 'poster-ready'
			: 'missing'
		await new Promise<void>((resolve) =>
			requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
		)
		if (
			!this.isConnected ||
			this.lifecycle.signal.aborted ||
			this.isHiddenForViewport()
		)
			return
		if (
			(!ambientMotionEnabled() || this.userPaused || this.eventDriven) &&
			!this.userPlayRequested
		) {
			this.settlePoster('paused')
		}
	}

	private settlePoster(state: 'failed' | 'paused') {
		this.settled = true
		this.dataset.mediaState = state
		this.cancelFrame()
		this.videoEl.pause()
		this.hlsInstance?.stopLoad()
		if (state === 'failed') {
			this.dataset.ucHasPlayed = 'false'
			this.userPlayRequested = false
			this.hlsInstance?.destroy()
			this.hlsInstance = undefined
			this.hlsReady = false
			this.videoEl.removeAttribute('src')
			this.videoEl.load()
			this.usingMp4Source = false
			this.preparation = undefined
		}
		this.closest('art-direction')?.classList.add('loaded')
		this.updateToggleState()
	}

	private cancelFrame() {
		window.clearTimeout(this.frameTimer)
		if (this.frameCallback !== undefined)
			this.videoEl.cancelVideoFrameCallback?.(this.frameCallback)
		this.frameCallback = undefined
		this.framePending = false
	}

	private awaitPresentedFrame() {
		if (this.framePending || this.dataset.ucHasPlayed === 'true') return
		this.framePending = true
		this.frameTimer = window.setTimeout(
			() => this.settlePoster('failed'),
			8000
		)
		const reveal = () => {
			if (
				!this.isConnected ||
				this.lifecycle.signal.aborted ||
				this.settled ||
				this.videoEl.paused
			)
				return
			this.cancelFrame()
			this.markMediaLoaded()
			this.dataset.mediaState = 'playing'
		}
		if (typeof this.videoEl.requestVideoFrameCallback === 'function') {
			this.frameCallback = this.videoEl.requestVideoFrameCallback(reveal)
		} else {
			const check = () => {
				if (!this.framePending) return
				if (
					this.videoEl.readyState >=
						HTMLMediaElement.HAVE_CURRENT_DATA &&
					this.videoEl.currentTime > 0
				)
					requestAnimationFrame(reveal)
				else requestAnimationFrame(check)
			}
			requestAnimationFrame(check)
		}
	}

	videoEl: HTMLVideoElement
	hlsReady: boolean
	hlsSource?: string
	mp4Source?: string
	initialized = false
	usingMp4Source: boolean
	hlsInstance?: Hls
	eventDriven: boolean
	eventEnabled: boolean
	autoplayDisabled: boolean
	limitHlsOnMobile: boolean
	nativeHlsSourcePromise?: Promise<string>
	wrapper?: ArtDirection
	toggleButton?: HTMLButtonElement
	toggleLabel?: HTMLElement
	muteToggleButton?: HTMLButtonElement
	muteToggleLabel?: HTMLElement
	controlsBound: boolean
	userPaused: boolean
	userPlayRequested: boolean
	autoplayReady: boolean
	autoplayReadyTimer?: number
	autoplayIntentBound: boolean
	onAutoplayIntentBound: EventListener
	retryAttempts: number
	retryInitTimer?: number
	onToggleClickBound: EventListener
	onVideoStateChangedBound: EventListener
	onMuteToggleClickBound: EventListener
	onVideoVolumeChangedBound: EventListener

	constructor() {
		super()
		this.videoEl = q.rs<HTMLVideoElement>('video', this)
		this.hlsSource = this.videoEl.getAttribute('data-hls-src') ?? undefined
		this.hlsReady = false
		this.mp4Source = this.videoEl.getAttribute('data-mp4-src') ?? undefined
		this.usingMp4Source = false
		this.initialized = true
		this.eventDriven = this.hasAttribute('data-event-driven')
		this.eventEnabled = false
		this.autoplayDisabled = this.hasAttribute('data-uc-autoplay-disabled')
		this.limitHlsOnMobile =
			this.hasAttribute('data-uc-limit-hls-on-mobile') &&
			window.matchMedia('(max-width: 989px)').matches
		this.nativeHlsSourcePromise = undefined
		this.playOnce = this.hasAttribute('data-uc-play-once')
		this.shortVideo = this.hasAttribute('data-uc-short-video')
		this.wrapper = undefined
		this.toggleButton = q.os<HTMLButtonElement>(
			'[data-uc-video-toggle]',
			this
		)
		this.toggleLabel = q.os<HTMLElement>(
			'[data-uc-video-toggle-label]',
			this
		)
		this.muteToggleButton = q.os<HTMLButtonElement>(
			'[data-uc-video-mute-toggle]',
			this
		)
		this.muteToggleLabel = q.os<HTMLElement>(
			'[data-uc-video-mute-toggle-label]',
			this
		)
		this.controlsBound = false
		this.userPaused = false
		this.userPlayRequested = false
		this.autoplayReady = this.eventDriven
		this.autoplayReadyTimer = undefined
		this.autoplayIntentBound = false
		this.onAutoplayIntentBound = this.onAutoplayIntent.bind(this)
		this.retryAttempts = 0
		this.retryInitTimer = undefined
		this.onToggleClickBound = this.onToggleClick.bind(this)
		this.onVideoStateChangedBound = this.onVideoStateChanged.bind(this)
		this.onMuteToggleClickBound = this.onMuteToggleClick.bind(this)
		this.onVideoVolumeChangedBound = this.updateMuteToggleState.bind(this)
	}

	override async connectedCallback() {
		super.connectedCallback()
		if (this.posterFirst && this.closest('[data-media-paused]'))
			this.userPaused = true
		this.lifecycle = new AbortController()
		if (this.posterFirst)
			this.videoEl.addEventListener(
				'error',
				() => this.handleVideoError(),
				{ signal: this.lifecycle.signal }
			)
		this.onConnectedCallback()
		this.bindControls()
		if (
			this.playOnce &&
			this.closest('art-direction')?.hasAttribute('data-uc-played-once')
		) {
			this.settleCompletedPoster()
		}
		void this.updateMuteControlAvailability()
		if (!this.eventDriven && !this.autoplayDisabled) {
			this.scheduleAutoplayReady()
		}
	}

	onConnectedCallback() {
		const mediaManager = window?.Ucoast?.mediaManager
		if (!mediaManager) {
			this.markForRetry()
			this.scheduleInitRetry()
			return
		}
		this.retryAttempts = 0
		this.clearInitRetry()
		this.unMarkForRetry()
		if (this.hlsSource || this.mp4Source) {
			mediaManager.addVideo(this)
		}
	}

	markForRetry() {
		this.setAttribute('data-retry', 'true')
	}

	// used by mediaManager to retry initialization
	unMarkForRetry() {
		this.removeAttribute('data-retry')
	}

	override async disconnectedCallback() {
		super.disconnectedCallback()
		this.lifecycle.abort()
		this.cancelFrame()
		this.videoEl.pause()
		this.videoEl.removeAttribute('src')
		this.videoEl.load()
		this.preparation = undefined
		this.clearInitRetry()
		this.clearAutoplayReady()
		this.unbindAutoplayIntent()
		this.unbindControls()
		if (this.hlsInstance) {
			this.hlsInstance.destroy()
		}
		window?.Ucoast?.mediaManager?.removeVideo(this)
	}

	// both playEvent methods are used by remote components
	async playEventOn() {
		if (!this.eventDriven) return
		this.eventEnabled = true
		this.autoplayReady = true
		await this.play()
	}

	playEventOff() {
		this.eventDriven = true // sometimes we need to convert auto-play to event-driven
		this.eventEnabled = false
		this.userPlayRequested = false
		this.pause()
	}

	isTouchingViewport() {
		const rect = this.getBoundingClientRect()

		const isVisible =
			rect.top < window.innerHeight &&
			rect.bottom > 0 &&
			rect.left < window.innerWidth &&
			rect.right > 0

		return isVisible
	}

	private playAttempt?: Promise<void>

	// The media manager, the visibility observer and the PDP's landing nudge
	// can all ask at once. Overlapping attempts each re-set the source, and the
	// reload aborts the play before it, so they share one attempt. A visitor's
	// own request that arrives mid-attempt still gets a turn if it ends paused.
	play(options: { userInitiated?: boolean } = {}): Promise<void> {
		if (options.userInitiated) this.userPlayRequested = true
		if (this.playAttempt) {
			if (!options.userInitiated) return this.playAttempt
			return this.playAttempt.then(() =>
				this.videoEl.paused ? this.play(options) : undefined
			)
		}
		const attempt = this.startPlayback(options).finally(() => {
			if (this.playAttempt === attempt) this.playAttempt = undefined
		})
		this.playAttempt = attempt
		return attempt
	}

	private async startPlayback(options: { userInitiated?: boolean }) {
		// all conditions that should prevent the video from playing must go here
		if (options.userInitiated) this.userPlayRequested = true
		const userRequestedPlayback =
			options.userInitiated === true || this.userPlayRequested
		if (!userRequestedPlayback && this.autoplayDisabled) return
		if (
			this.playOnce &&
			this.closest('art-direction')?.hasAttribute('data-uc-played-once')
		)
			return
		if (!this.isTouchingViewport()) return
		if (this.eventDriven && !this.eventEnabled) return
		if (!this.eventDriven && !this.autoplayReady && !userRequestedPlayback)
			return
		if (!userRequestedPlayback && !ambientMotionEnabled()) return
		if (!userRequestedPlayback && this.userPaused) return
		if (this.isHiddenForViewport()) return
		if (document.hidden || !this.isConnected) return
		if (this.posterFirst) {
			if (this.settled && !userRequestedPlayback) return
			await this.prepare()
			if (
				!this.isConnected ||
				this.isHiddenForViewport() ||
				!this.isTouchingViewport()
			)
				return
			if (this.settled && !userRequestedPlayback) return
			this.settled = false
			this.hlsInstance?.startLoad()
			this.awaitPresentedFrame()
		}
		const mediaManager = window?.Ucoast?.mediaManager
		const canPlayNativeHls =
			this.videoEl.canPlayType('application/vnd.apple.mpegurl') !== ''
		if (
			this.limitHlsOnMobile &&
			this.hlsSource &&
			canPlayNativeHls &&
			!this.videoEl.src
		) {
			this.videoEl.src = await this.resolveNativeHlsSource()
		}
		if (this.hlsSource && !this.videoEl.src && !this.usingMp4Source) {
			if (!mediaManager && canPlayNativeHls) {
				this.videoEl.src = this.hlsSource
			} else if (mediaManager && !mediaManager.hlsRequired) {
				this.videoEl.src = this.hlsSource
			}
		}
		if (
			!mediaManager &&
			this.hlsSource &&
			this.mp4Source &&
			!canPlayNativeHls &&
			!this.usingMp4Source
		) {
			this.videoEl.src = this.mp4Source
			this.usingMp4Source = true
		}
		if (isVideoPlaying(this.videoEl)) return
		if (
			this.hlsSource &&
			mediaManager?.hlsRequired &&
			!this.hlsReady &&
			!this.usingMp4Source
		) {
			await mediaManager.ensureHlsFor(this)
		}
		if (this.isWaitingForHlsLibrary()) return
		if (this.eventDriven && !this.eventEnabled) return
		if (!userRequestedPlayback && !ambientMotionEnabled()) return
		if (this.isHiddenForViewport()) return
		if (percentageSeen(this.videoEl) <= 0) return

		// check for updates that must happen before playing that can be done synchronously
		if (!this.hlsSource && !this.usingMp4Source && this.mp4Source) {
			this.videoEl.src = this.mp4Source
			this.usingMp4Source = true
		}
		// play video w/native html5 video element
		try {
			await this.videoEl.play()
		} catch (error) {
			if (
				error instanceof DOMException &&
				error.name === 'AbortError' &&
				this.videoEl.paused
			)
				return
			if (error instanceof DOMException) {
				this.revealPosterAfterPlayRejection(
					error.name === 'NotAllowedError'
				)
				return
			}
			throw error
		}
		if (
			userRequestedPlayback &&
			this.hasAttribute('data-uc-autoplay-blocked')
		) {
			this.autoplayDisabled = this.hasAttribute(
				'data-uc-autoplay-disabled'
			)
			this.removeAttribute('data-uc-autoplay-blocked')
		}
		if (!this.posterFirst) {
			this.dataset.mediaState = 'playing'
			this.markMediaLoaded()
		}
		this.updateToggleState()
	}

	private revealPosterAfterPlayRejection(policyBlocked: boolean) {
		this.autoplayDisabled = true
		this.clearAutoplayReady()
		this.unbindAutoplayIntent()
		this.cancelFrame()
		this.settled = true
		this.videoEl.pause()
		this.userPlayRequested = false
		this.dataset.ucHasPlayed = 'false'
		this.dataset.mediaState = policyBlocked
			? 'autoplay-blocked'
			: 'failed'
		this.toggleAttribute('data-uc-autoplay-blocked', policyBlocked)

		this.revealPoster()

		if (this.toggleButton) {
			this.toggleButton.hidden = false
			this.setAttribute('data-uc-show-controls', '')
		}
		this.updateToggleState()
	}

	private revealPoster() {
		const wrapper = this.closest<HTMLElement>('art-direction')
		wrapper?.classList.add('loaded')
		const poster = wrapper?.querySelector<HTMLImageElement>('img')
		if (poster?.complete && poster.naturalWidth > 0) {
			poster.style.visibility = ''
			const fadeTarget =
				poster.parentElement instanceof HTMLPictureElement
					? poster.parentElement
					: poster
			fadeTarget.style.opacity = '1'
		}
	}

	async resolveNativeHlsSource(): Promise<string> {
		if (!this.hlsSource) return ''
		if (!this.nativeHlsSourcePromise) {
			this.nativeHlsSourcePromise = (async () => {
				const masterUrl = new URL(this.hlsSource!, window.location.href)
				try {
					const response = await fetch(masterUrl, {
						signal: this.lifecycle.signal,
					})
					if (!response.ok) return masterUrl.href
					const variant = selectHlsVariant(
						await response.text(),
						masterUrl,
						MOBILE_AUTOPLAY_HLS_MAX_HEIGHT
					)
					return variant ?? masterUrl.href
				} catch {
					return masterUrl.href
				}
			})()
		}
		return this.nativeHlsSourcePromise
	}
	pause() {
		if (this.posterFirst) {
			this.hlsInstance?.stopLoad()
			this.cancelFrame()
			this.dataset.mediaState = 'paused'
			if (
				(!this.isTouchingViewport() || this.isHiddenForViewport()) &&
				!this.hlsInstance
			) {
				this.videoEl.removeAttribute('src')
				this.videoEl.load()
				this.usingMp4Source = false
				this.preparation = undefined
				this.dataset.ucHasPlayed = 'false'
			}
		}
		if (isVideoPlaying(this.videoEl)) {
			this.videoEl.pause()
		}
		if (this.posterFirst && this.userPaused) this.settlePoster('paused')
		this.updateToggleState()
	}

	isWaitingForHlsLibrary() {
		if (!this.hlsSource) return false
		if (this.usingMp4Source) return false
		const mediaManager = window?.Ucoast?.mediaManager
		if (!mediaManager) {
			const canPlayNativeHls =
				this.videoEl.canPlayType('application/vnd.apple.mpegurl') !== ''
			return !canPlayNativeHls && !this.mp4Source
		}
		if (!mediaManager.hlsRequired) return false
		if (!this.hlsReady) return true
		return !mediaManager.hlsLibraryLoaded
	}

	scheduleInitRetry() {
		if (this.retryInitTimer !== undefined) return
		if (this.retryAttempts >= 50) return
		this.retryAttempts += 1
		this.retryInitTimer = window.setTimeout(() => {
			this.retryInitTimer = undefined
			this.onConnectedCallback()
			if (!this.eventDriven && !this.autoplayDisabled) {
				this.scheduleAutoplayReady()
			}
		}, 120)
	}

	clearInitRetry() {
		if (this.retryInitTimer === undefined) return
		window.clearTimeout(this.retryInitTimer)
		this.retryInitTimer = undefined
	}

	private handleVideoError() {
		if (this.videoEl.error?.code === MediaError.MEDIA_ERR_ABORTED) return

		if (this.hlsSource && this.mp4Source && !this.usingMp4Source) {
			this.settled = false
			this.preparation = undefined
			this.useMp4Fallback()
			if (this.usingMp4Source) {
				void this.play({ userInitiated: this.userPlayRequested })
				return
			}
		}

		this.settlePoster('failed')
	}

	useMp4Fallback() {
		if (this.posterFirst && (this.settled || !this.isConnected)) return
		if (!this.mp4Source || this.usingMp4Source) return
		this.usingMp4Source = true
		this.videoEl.src = this.mp4Source
		this.hlsReady = true
		window?.Ucoast?.mediaManager?.addVideo(this)
	}

	scheduleAutoplayReady() {
		if (this.posterFirst) {
			this.autoplayReady = true
			return
		}
		if (this.autoplayReady || this.autoplayReadyTimer !== undefined) return
		if (
			shouldHoldAutoplayForUserIntent(
				this.hasAttribute('data-uc-autoplay-on-mobile')
			)
		) {
			this.revealPoster()
			this.bindAutoplayIntent()
			return
		}
		const enableAutoplay = () => {
			if (!this.isConnected) return
			const autoplayDelay =
				this.hasAttribute('data-uc-autoplay-on-mobile') &&
				window.matchMedia('(max-width: 767px)').matches
					? 0
					: 1200
			this.autoplayReadyTimer = window.setTimeout(() => {
				this.autoplayReadyTimer = undefined
				if (!this.isConnected) return
				this.autoplayReady = true
				void this.play()
			}, autoplayDelay)
		}

		if (document.readyState === 'complete') {
			enableAutoplay()
			return
		}
		window.addEventListener('load', enableAutoplay, { once: true })
	}

	clearAutoplayReady() {
		if (this.autoplayReadyTimer === undefined) return
		window.clearTimeout(this.autoplayReadyTimer)
		this.autoplayReadyTimer = undefined
	}

	bindAutoplayIntent() {
		if (this.autoplayIntentBound) return
		const options: AddEventListenerOptions = { once: true, passive: true }
		window.addEventListener(
			'pointerdown',
			this.onAutoplayIntentBound,
			options
		)
		window.addEventListener('keydown', this.onAutoplayIntentBound, {
			once: true,
		})
		window.addEventListener('scroll', this.onAutoplayIntentBound, options)
		this.autoplayIntentBound = true
	}

	unbindAutoplayIntent() {
		if (!this.autoplayIntentBound) return
		window.removeEventListener('pointerdown', this.onAutoplayIntentBound)
		window.removeEventListener('keydown', this.onAutoplayIntentBound)
		window.removeEventListener('scroll', this.onAutoplayIntentBound)
		this.autoplayIntentBound = false
	}

	onAutoplayIntent() {
		this.unbindAutoplayIntent()
		this.autoplayReady = true
		void this.play()
	}

	async onLibraryLoad() {
		if (this.posterFirst && (this.settled || !this.isConnected)) return
		// don't run if this isn't an hls video or if hls is already initalized for this element
		if (!this.hlsSource) return
		if (this.hlsInstance) return
		const mediaManager = window?.Ucoast?.mediaManager
		if (!mediaManager?.Hls) return
		if (!mediaManager.hlsLibIsSupported) {
			this.useMp4Fallback()
			return
		}
		// initialize hls - this is a 3ms process that doesn't load any video data until a video is played
		this.hlsInstance = new mediaManager.Hls.default({
			autoStartLoad: !this.posterFirst,
			videoPreference: {
				preferHDR: true,
			},
			capLevelToPlayerSize: !this.limitHlsOnMobile,
			ignoreDevicePixelRatio: true,
			backBufferLength: 2,
			maxBufferLength: 6,
			maxMaxBufferLength: 8,
		})
		const hlsInstance = this.hlsInstance
		hlsInstance.on('hlsManifestParsed', () => {
			if (this.limitHlsOnMobile) {
				const mobileLevelCap = hlsInstance.levels.reduce(
					(cap: number, level: { height: number }, index: number) =>
						level.height <= MOBILE_AUTOPLAY_HLS_MAX_HEIGHT
							? index
							: cap,
					0
				)
				hlsInstance.autoLevelCapping = mobileLevelCap
			}
			this.hlsReady = true
			void this.play()
		})
		await this.hlsInstance.loadSource(this.hlsSource)
		await this.hlsInstance.attachMedia(this.videoEl)
		// all videos present in DOM at page load are added to the mediaManager.videos array, but if this video was added to the DOM later, it needs to be added to the array

		mediaManager.addVideo(this)
	}

	isHiddenForViewport() {
		const rect = this.videoEl.getBoundingClientRect()
		return rect.height < 1 || rect.width < 1
	}

	bindControls() {
		if (this.controlsBound) return
		if (this.toggleButton) {
			this.toggleButton.addEventListener('click', this.onToggleClickBound)
		}
		if (this.muteToggleButton) {
			this.muteToggleButton.addEventListener(
				'click',
				this.onMuteToggleClickBound
			)
			this.videoEl.addEventListener(
				'volumechange',
				this.onVideoVolumeChangedBound
			)
		}
		this.videoEl.addEventListener('play', this.onVideoStateChangedBound)
		this.videoEl.addEventListener('pause', this.onVideoStateChangedBound)
		this.videoEl.addEventListener('ended', this.onVideoStateChangedBound)
		this.controlsBound = true
		this.updateShortVideoControl()
		this.updateToggleState()
		this.updateMuteToggleState()
	}

	unbindControls() {
		if (!this.controlsBound) return
		if (this.toggleButton) {
			this.toggleButton.removeEventListener(
				'click',
				this.onToggleClickBound
			)
		}
		if (this.muteToggleButton) {
			this.muteToggleButton.removeEventListener(
				'click',
				this.onMuteToggleClickBound
			)
			this.videoEl.removeEventListener(
				'volumechange',
				this.onVideoVolumeChangedBound
			)
		}
		this.videoEl.removeEventListener('play', this.onVideoStateChangedBound)
		this.videoEl.removeEventListener('pause', this.onVideoStateChangedBound)
		this.videoEl.removeEventListener('ended', this.onVideoStateChangedBound)
		this.controlsBound = false
	}

	onToggleClick(event: Event) {
		event.preventDefault()
		const shouldPause = this.toggleButton?.dataset.state === 'playing'
		const siblings = this.posterFirst
			? this.closest('art-direction')?.querySelectorAll<UcoastVideo>(
					'ucoast-video'
				)
			: undefined
		for (const video of siblings ?? [this]) {
			video.userPaused = shouldPause
			video.userPlayRequested = !shouldPause
			if (shouldPause) video.pause()
			else {
				if (video.videoEl.ended) video.videoEl.currentTime = 0
				video.settled = false
				video.preparation = undefined
				video.eventEnabled = true
				video.autoplayReady = true
			}
		}
		if (!shouldPause) void this.play({ userInitiated: true })
	}

	onMuteToggleClick(event: Event) {
		event.preventDefault()
		this.videoEl.muted = !this.videoEl.muted
	}

	onVideoStateChanged() {
		if (this.playOnce && this.videoEl.ended) {
			this.finishPlaybackOnce()
			return
		}
		if (isVideoPlaying(this.videoEl) && !this.posterFirst) {
			this.markMediaLoaded()
		}
		this.updateShortVideoControl()
		this.updateToggleState()
	}

	private finishPlaybackOnce() {
		const wrapper = this.closest('art-direction')
		wrapper?.setAttribute('data-uc-played-once', '')
		const siblings = wrapper?.querySelectorAll<UcoastVideo>('ucoast-video')
		for (const video of siblings ?? [this]) {
			if (!video.playOnce) continue
			video.settleCompletedPoster()
		}
	}

	private settleCompletedPoster() {
		this.userPaused = true
		this.userPlayRequested = false
		this.settlePoster('paused')
		this.dataset.mediaState = 'ended'
		this.dataset.ucHasPlayed = 'false'
		this.updateShortVideoControl()
		this.updateToggleState()
	}

	private updateShortVideoControl() {
		if (!this.toggleButton) return
		const completedPlayOnce =
			this.playOnce &&
			this.closest('art-direction')?.hasAttribute('data-uc-played-once') ===
				true
		const hideControl =
			completedPlayOnce || (this.shortVideo && ambientMotionEnabled())
		this.toggleButton.hidden = hideControl
		this.toggleAttribute('data-uc-show-controls', !hideControl)
	}

	markMediaLoaded() {
		this.setAttribute('data-uc-has-played', 'true')
		const wrapper =
			this.wrapper ??
			q.oc<ArtDirection & HTMLElement>(this, 'art-direction')
		if (!wrapper) return
		this.wrapper = wrapper
		wrapper.onMediaLoad()
	}

	updateToggleState() {
		if (!this.toggleButton) return
		const expectsAutoplay =
			this.posterFirst &&
			!this.userPaused &&
			!this.eventDriven &&
			!this.autoplayDisabled &&
			ambientMotionEnabled() &&
			this.dataset.mediaState !== 'failed' &&
			this.dataset.mediaState !== 'paused'
		const isPaused = !isVideoPlaying(this.videoEl) && !expectsAutoplay
		const label =
			this.posterFirst && this.dataset.mediaState === 'failed'
				? this.toggleButton.dataset.labelRetry ??
					this.toggleButton.dataset.labelPlay ??
					'Play video'
				: isPaused
					? this.toggleButton.dataset.labelPlay ?? 'Play video'
					: this.toggleButton.dataset.labelPause ?? 'Pause video'
		this.toggleButton.setAttribute(
			'data-state',
			isPaused ? 'paused' : 'playing'
		)
		this.toggleButton.setAttribute('aria-label', label)
		if (this.toggleLabel) {
			this.toggleLabel.textContent = label
		}
	}

	updateMuteToggleState() {
		if (!this.muteToggleButton) return
		const isMuted = this.videoEl.muted
		const label = isMuted
			? this.muteToggleButton.dataset.labelUnmute ?? 'Unmute video'
			: this.muteToggleButton.dataset.labelMute ?? 'Mute video'
		this.muteToggleButton.setAttribute(
			'data-state',
			isMuted ? 'muted' : 'unmuted'
		)
		this.muteToggleButton.setAttribute('aria-label', label)
		this.muteToggleButton.setAttribute(
			'aria-pressed',
			isMuted ? 'true' : 'false'
		)
		if (this.muteToggleLabel) {
			this.muteToggleLabel.textContent = label
		}
	}

	private async updateMuteControlAvailability() {
		if (!this.muteToggleButton) return
		if (!this.hasAttribute('data-uc-hide-mute-when-silent')) return
		const hasAudio = this.hlsSource
			? await hlsSourceHasAudio(this.hlsSource, this.lifecycle.signal)
			: undefined
		if (!this.isConnected || this.lifecycle.signal.aborted) return

		const isSilent = hasAudio === false
		this.toggleAttribute('data-uc-show-mute-toggle', !isSilent)
		this.muteToggleButton.hidden = isSilent
		this.muteToggleButton.dataset.audioState = isSilent
			? 'silent'
			: hasAudio === true
				? 'audible'
				: 'unknown'
	}
}
